/**
 * Customer-Portal shared types — Story C1 (Customer Portal data model).
 *
 * Placed in packages/shared (not internal-only) because the UI will consume these
 * in Phase 3 (customer-portal frontend). Unlike renewals-math which is api-internal,
 * portal status + feature keys are UI-visible.
 *
 * No magic values — import these constants, never inline the strings.
 * [Source: docs/architecture/coding-standards.md#1-cross-cutting-conventions-all-languages]
 *
 * [Source: docs/concept/2026-06-26_post-stefan-roadmap-and-module-specs.md §3]
 * [Source: Story C1 spec §4 — "packages/shared/src/customer-portal.ts"]
 */

import type { LineOfBusiness, PolicyStatus, PaymentFrequency } from './policy';
import type { InvoiceType } from './invoice';
import type { BrokerBrandingColors } from './broker-branding';

// ── Portal access status ──────────────────────────────────────────────────────

/**
 * PORTAL_ACCESS_STATUS: the states of a customer's portal access lifecycle.
 *
 * not_invited → invited (C2 auto-invite job sets this) → active (C3 first-login sets this).
 * P6-S7 (Stefan 10.4): active → lesend (the nightly run, the customer turned inaktiv; login and reads
 * only, until access_expires_at) → deaktiviert (after the Nachlauf; no login). An invited access of an
 * inaktiv customer goes to deaktiviert at once. The login accepts invited, active, lesend.
 *
 * TEXT, not a PG enum — state machine lives in NestJS (mirrors renewal_signal.status convention).
 * No magic values — import this constant, never inline the strings.
 *
 * [Source: Story C1 spec §1.2 — status column design]
 */
export const PORTAL_ACCESS_STATUS = {
  NOT_INVITED: 'not_invited',
  INVITED:     'invited',
  ACTIVE:      'active',
  LESEND:      'lesend',
  DEAKTIVIERT: 'deaktiviert',
} as const;

/** Union type of all valid portal access status values. */
export type PortalAccessStatusValue = (typeof PORTAL_ACCESS_STATUS)[keyof typeof PORTAL_ACCESS_STATUS];

/**
 * Flat readonly array of all portal access status values (in lifecycle order).
 * Used by @IsIn() DTO validators and response filters.
 */
export const PORTAL_ACCESS_STATUS_VALUES = Object.values(PORTAL_ACCESS_STATUS) as readonly PortalAccessStatusValue[];

// ── Portal feature toggles ────────────────────────────────────────────────────

/**
 * PORTAL_FEATURE_KEYS — ordered list of all feature toggle keys.
 *
 * Each key maps to a boolean in the `feature_toggles` JSONB column.
 * Drives which portal sections the customer can see after activation.
 *
 * No magic values — import this constant, never inline the key strings.
 *
 * [Source: Story C1 spec §1.1 — feature_toggles shape]
 */
export const PORTAL_FEATURE_KEYS = [
  'policen',
  'rechnungen',
  'schadenmeldung',
  'feedback',
  'umfragen',
  'beratung',
] as const;

/** The type-safe shape of the feature_toggles JSONB blob. */
export type PortalFeatureToggles = {
  [K in (typeof PORTAL_FEATURE_KEYS)[number]]: boolean;
};

/**
 * PortalFeatureKey — a single valid feature-toggle key.
 *
 * The typed parameter of @RequirePortalFeature: a typo'd feature key becomes a COMPILE
 * error (mirrors PortalScopeKey for @RequirePortalScope). Additive alias over the existing
 * PORTAL_FEATURE_KEYS catalog — no existing type is changed.
 */
export type PortalFeatureKey = (typeof PORTAL_FEATURE_KEYS)[number];

// ── Branding DTO ──────────────────────────────────────────────────────────────

/**
 * PortalBrandingDto — the shape of the portal_config.branding JSONB column.
 *
 * logoRef: a string reference (file path / object key) — NO blob stored in the DB.
 * primaryHex / secondaryHex: #RRGGBB hex colour strings or null (unset).
 *
 * [Source: Story C1 spec §1.1 — branding column]
 */
export interface PortalBrandingDto {
  logoRef:      string | null;
  primaryHex:   string | null;
  secondaryHex: string | null;
}

// ── Invite criteria DTO ───────────────────────────────────────────────────────

/**
 * PortalInviteCriteriaDto — the shape of the portal_config.invite_criteria JSONB column.
 *
 * kundenStatuses: array of CustomerStatusValue strings; only customers in these statuses
 *   are eligible for auto-invite (C2). Validated against CUSTOMER_STATUS_VALUES in service.
 * requireEmail: when true, C2 skips customers with no email address on file.
 *
 * [Source: Story C1 spec §1.1 — invite_criteria column]
 */
export interface PortalInviteCriteriaDto {
  kundenStatuses: string[];  // CustomerStatusValue[] — validated in service against CUSTOMER_STATUS_VALUES
  requireEmail:   boolean;
}

// ── Full config response DTO ──────────────────────────────────────────────────

/**
 * PortalConfigDto — full response shape for GET/PUT /admin/customer-portal.
 *
 * tenant_id is NEVER present in the response (server-side only; rule #1).
 * is_default: true  = no stored row; in-code default surfaced (version: null, updated_at: null).
 * is_default: false = stored row; version is the current version number.
 *
 * Wire-format uses snake_case (matching the DB column names).
 *
 * [Source: Story C1 spec §7 — GET/PUT response shape]
 */
export interface PortalConfigDto {
  enabled:               boolean;
  branding:              PortalBrandingDto;
  feature_toggles:       Partial<PortalFeatureToggles>;
  invite_criteria:       PortalInviteCriteriaDto;
  reminder_cascade:      number[];
  access_retention_days: number;
  /** Validity of an activation link in days (7..60, default 30). */
  activation_link_days:  number;
  version:               number | null;
  is_default:            boolean;
  updated_at:            string | null;
}

// ── Story C3 — Passwordless Auth shared types ─────────────────────────────────

/**
 * PORTAL_REQUEST_CODE_STATUS — the uniform response body for request-code.
 *
 * Always returned regardless of whether the email exists, is invited, or is unknown
 * (no account enumeration — same shape in all branches).
 *
 * [Source: Story C3 spec §2]
 */
export const PORTAL_REQUEST_CODE_STATUS = { CODE_SENT: 'code_sent' } as const;

/**
 * PORTAL_TOKEN_ISS — JWT issuer claim for portal session tokens.
 *
 * Distinct from the broker OIDC issuer so a portal token CANNOT satisfy JwtAuthGuard.
 * [Source: Story C3 spec §2]
 */
export const PORTAL_TOKEN_ISS = 'brokly-portal' as const;

/**
 * PORTAL_TOKEN_AUD — JWT audience claim for portal session tokens.
 *
 * Distinct from the broker OIDC audience so a portal token CANNOT satisfy JwtAuthGuard.
 * [Source: Story C3 spec §2]
 */
export const PORTAL_TOKEN_AUD = 'brokly-portal-client' as const;

/**
 * PortalBrokerSelectorEntry — an opaque broker entry in the multi-broker selector response.
 *
 * `selection` is a short-lived signed handle for one tenant — NOT the tenantId or subjectId.
 * The UI shows brokerName/brandingLogoRef; when the customer picks, it submits `selection`
 * to POST /portal/auth/choose-broker.
 *
 * [Source: Story C3 spec §2]
 */
export interface PortalBrokerSelectorEntry {
  /** Opaque signed handle — NOT the tenantId/subjectId. Short-lived per-tenant signed blob. */
  selection: string;
  /** Broker display name (tenant.name), safe to show the customer. */
  brokerName: string;
  /** Branding logo reference (file path / object key) or null if no branding set. */
  brandingLogoRef: string | null;
}

/**
 * PortalVerifyMultiResponse — returned when the customer's email is invited at multiple brokers.
 *
 * The customer must explicitly choose a broker before a session is issued (see chooseBroker).
 * `continuation` is a short-lived signed token binding the verified email+code consumption
 * to the selection step — prevents re-entering the code on chooseBroker.
 *
 * [Source: Story C3 spec §2]
 */
export interface PortalVerifyMultiResponse {
  outcome: 'select_broker';
  brokers: PortalBrokerSelectorEntry[];
  /** Continuation token binding the verified identity set to the selection step. */
  continuation: string;
}

/**
 * PortalSessionResponse — returned when a session is successfully issued.
 *
 * `token` is a portal JWT (HS256, own iss/aud/secret — NOT a broker OIDC token).
 * `features` contains only the toggled-on feature keys at issue time (least-privilege).
 *
 * [Source: Story C3 spec §2]
 */
export interface PortalSessionResponse {
  outcome: 'session';
  /** Portal JWT (NOT a broker OIDC token — own iss/aud/secret). */
  token: string;
  /** ISO 8601 expiry timestamp — matches the JWT `exp` claim. */
  expiresAt: string;
  /** Only toggled-on feature keys (least-privilege — false toggles are absent). */
  features: Partial<PortalFeatureToggles>;
  /** Broker display name (tenant.name) shown after login. */
  brokerName: string;
  /**
   * The broker's three brand colours (W4 — portal theme). Each is a #RRGGBB hex or
   * null (unset → the FE falls back to the Brokly default). ABSENT on legacy responses
   * issued before W4 — the portal theme treats absent/null exactly like "use the default"
   * so the portal is themed on-brand regardless. DISPLAY ONLY (never in the JWT): it drives
   * the CSS variables the portal shell paints (primary = buttons/links/active, header =
   * the band behind the logo, secondary = accents); body text/bg/borders stay fixed/readable.
   * [Source: packages/shared/src/broker-branding.ts (canonical colour shape)]
   */
  branding?: BrokerBrandingColors;
  /**
   * Granted portal scopes for this session (V10-D).
   * Primary holder → all PORTAL_SCOPE_KEYS; grant session → the grant's scope set.
   */
  scopes: string[];
  /**
   * True IFF this is the primary holder (Main-User / Zeichnungsberechtigt) session —
   * the CANONICAL marker, mirroring the backend PortalSessionClaims.gid === null
   * (PortalPrincipal grantId === null). A grant (Treuhänder / weitere Person) is false
   * EVEN when it was issued the full scope set — scope breadth is NOT the discriminator.
   * The FE MUST label the role from THIS flag, never infer "primary" from holding all scopes.
   * KEPT on the wire beside `role` (V10-O) for compat — `role` is the label source now.
   * [Source: apps/api/src/modules/customer-portal/portal-session.guard.ts#PortalPrincipal]
   */
  isPrimary: boolean;
  /**
   * The session's member role (V10-O — 2026-07-14 decisions):
   *   primary holder (gid null, logged in via invite_email) → 'admin';
   *   member grant with role='admin' → 'admin';
   *   every other grant (member 'mitarbeiter', Treuhänder/weitere) → 'mitarbeiter'.
   * NO rights delta between the roles except member/invite management (admin-only).
   * The FE labels + adminOnly-gates from THIS field — the server re-enforces everywhere.
   */
  role: PortalMemberRole;
  /**
   * The logged-in person's display name (V10-U — set at V10-O activation,
   * portal_account.display_name). DISPLAY ONLY (dashboard greeting) — identity stays
   * in the JWT. Null on legacy passwordless data / when no portal_account exists;
   * the FE falls back to a name-less greeting and NEVER fabricates a name.
   */
  displayName: string | null;
}

/** Discriminated union of verify-code outcomes. */
export type PortalVerifyResponse = PortalVerifyMultiResponse | PortalSessionResponse;

/**
 * PortalSessionClaims — the shape of the portal JWT payload (server-internal contract).
 *
 * `jose` adds standard claims (iat, exp, iss, aud) automatically.
 * `sid` = portal_session.id — the server-side session row id for revocation checks.
 * `sid_sub` is named to avoid clashing with the standard `sid` (which is our session id).
 *
 * Claim names are short to minimise JWT size.
 *
 * Story V10-D adds `scp` (granted portal scopes) + `gid` (portal_grant id):
 *   gid = null  → primary holder session (implicitly passes EVERY scope check).
 *   gid = <uuid> → grant session (Treuhänder / weitere Person); scp is the fence.
 *
 * [Source: Story C3 spec §2]
 * [Source: Story V10-D AC 1]
 */
export interface PortalSessionClaims {
  /** portal_session.id — the server-side session row (revocable). */
  sid: string;
  /** tenantId (the broker tenant the customer authenticated against). */
  ten: string;
  /** subject_type ('person' | 'firma'). */
  sty: string;
  /** subject_id (person.id or firma.id). Named sid_sub to avoid clashing with `sid`. */
  sid_sub: string;
  /** Enabled feature keys (only toggled-on; matches features in PortalSessionResponse). */
  feat: string[];
  /** Granted portal scopes (V10-D). Primary holder sessions carry all PORTAL_SCOPE_KEYS. */
  scp: string[];
  /** portal_grant.id for grant sessions; null = primary holder (all scopes implied). V10-D. */
  gid: string | null;
  /**
   * The session's member role (V10-O): 'admin' | 'mitarbeiter'.
   * Legacy (pre-V10-O) tokens carry no rol claim — verify() fail-closed-defaults it to
   * gid === null ? 'admin' : 'mitarbeiter' (a grant can never default to admin).
   */
  rol: PortalMemberRole;
  /**
   * portal_account.id of the logged-in person (V10-O); null on legacy sessions issued
   * before the account model existed. Consumed by change-password + the Sicherheitscenter.
   */
  aid: string | null;
}

// ── Story V10-D — Portal scopes + grants (Kap. 20.2) ──────────────────────────

/**
 * PORTAL_SCOPE_KEYS — ordered list of all portal permission scopes (Kap. 20.2).
 *
 * A portal GRANT (invited Treuhänder / weitere Person) carries a subset of these;
 * the primary holder implicitly holds ALL of them (gid = null on the session).
 *
 * Scope semantics (D-PORTAL-SCOPES — fixed at draft time, do NOT invent new scopes):
 *   'documents.read' — list/read portal deliveries + documents
 *   'policies.read'  — read policy data (endpoint arrives with the portal frontend)
 *   'invoices.read'  — read invoice data (endpoint arrives with the portal frontend)
 *   'sign'           — participate in signing flows (endpoint arrives with V10-C portal leg)
 *   'upload'         — upload documents (endpoint arrives with the portal frontend)
 *   'messages'       — send Nachrichten/Anfragen + Stammdaten-Änderungswünsche (intake)
 *
 * SEPARATE axis from the broker RBAC PERMISSION_CATALOG — portal users are NOT app_users.
 * No magic values — import this constant, never inline the key strings.
 *
 * [Source: Story V10-D AC 1 + Dev Notes D-PORTAL-SCOPES]
 */
export const PORTAL_SCOPE_KEYS = [
  'documents.read',
  'policies.read',
  'invoices.read',
  'sign',
  'upload',
  'messages',
] as const;

/** Union type of all valid portal scope keys. */
export type PortalScopeKey = (typeof PORTAL_SCOPE_KEYS)[number];

/**
 * Flat readonly array of all portal scope values (same order as PORTAL_SCOPE_KEYS).
 * Used by @IsIn() DTO validators and the primary-holder full-scope session issue.
 */
export const PORTAL_SCOPE_VALUES = [...PORTAL_SCOPE_KEYS] as readonly PortalScopeKey[];

/**
 * PORTAL_GRANT_STATUS — the three states of a customer-initiated grant lifecycle.
 *
 * invited → active (first successful passwordless login) → revoked (customer or broker).
 * Re-invite after revoke creates a NEW row (partial-unique index excludes 'revoked').
 *
 * TEXT, not a PG enum — state machine lives in NestJS (mirrors PORTAL_ACCESS_STATUS).
 *
 * [Source: Story V10-D AC 3/4/5]
 */
export const PORTAL_GRANT_STATUS = {
  INVITED: 'invited',
  ACTIVE:  'active',
  REVOKED: 'revoked',
} as const;

/** Union type of all valid portal grant status values. */
export type PortalGrantStatusValue = (typeof PORTAL_GRANT_STATUS)[keyof typeof PORTAL_GRANT_STATUS];

/** Flat readonly array of all portal grant status values (in lifecycle order). */
export const PORTAL_GRANT_STATUS_VALUES = Object.values(PORTAL_GRANT_STATUS) as readonly PortalGrantStatusValue[];

/**
 * PORTAL_GRANT_ROLE_LABELS — role labels for invited persons (Kap. 20.2 role table).
 *
 * 'treuhaender' — Treuhänder (ASCII identifier; label rendered as "Treuhänder" in UI)
 * 'weitere'     — weitere berechtigte Person
 *
 * [Source: Story V10-D AC 3; concept Kap. 20.2 role table]
 */
export const PORTAL_GRANT_ROLE_LABELS = ['treuhaender', 'weitere'] as const;

/** Union type of all valid portal grant role labels. */
export type PortalGrantRoleLabel = (typeof PORTAL_GRANT_ROLE_LABELS)[number];

// ── Story V10-O — Portal auth v2 + Admin/Mitarbeiter roles (2026-07-14 decisions) ──

/**
 * PORTAL_MEMBER_ROLES — the EXACTLY-TWO member roles of a customer portal account
 * (2026-07-14 decision, V10.1).
 *
 * 'admin'       — full member rights PLUS member/invite management. The FIRST person
 *                 (who logs in via the customer's designated invite_email) is always admin.
 * 'mitarbeiter' — IDENTICAL rights to admin everywhere EXCEPT member/invite management.
 *
 * Rights are equal by design — scopes keep gating resources; the role gates ONLY the
 * member-management surfaces. TEXT column (app-validated, rule #2) — no PG enum.
 *
 * [Source: docs/concept/2026-07-15_v10.1-portal-prio-and-invite-decisions.md — 2026-07-14 decisions]
 */
export const PORTAL_MEMBER_ROLES = ['admin', 'mitarbeiter'] as const;

/** Union type of the two portal member roles. */
export type PortalMemberRole = (typeof PORTAL_MEMBER_ROLES)[number];

/** Flat readonly array of all portal member role values (same order as PORTAL_MEMBER_ROLES). */
export const PORTAL_MEMBER_ROLE_VALUES = [...PORTAL_MEMBER_ROLES] as readonly PortalMemberRole[];

/**
 * PORTAL_GRANT_ROLE_LABEL_MEMBER — the role_label of a MEMBER grant (V10-O).
 *
 * Member grants (Admin/Mitarbeiter invited by an Admin) live in portal_grant like the
 * Treuhänder path but carry role_label='mitglied' + role ∈ PORTAL_MEMBER_ROLES + the FULL
 * scope set. Deliberately NOT added to PORTAL_GRANT_ROLE_LABELS — the Treuhänder DTO keeps
 * validating against the original two labels, and the /portal/invites surface rejects
 * member rows (they are managed via /portal/members — sibling story V10-P).
 *
 * [Source: Story V10-O AC 5]
 */
export const PORTAL_GRANT_ROLE_LABEL_MEMBER = 'mitglied' as const;

/**
 * PORTAL_ACTIVATE_STATUS — the uniform success body for POST /portal/auth/activate.
 * Mirrors PORTAL_REQUEST_CODE_STATUS (no magic strings at call sites).
 * [Source: Story V10-O AC 2]
 */
export const PORTAL_ACTIVATE_STATUS = { ACTIVATED: 'activated' } as const;

// ── Story V10-P — Portal member management (Admin invites, Kap. 20.2/20.7.21) ──

/**
 * PortalMemberResponse — one member of a customer account as returned by the
 * /portal/members endpoints (Story V10-P).
 *
 * AS-BUILT model (V10-O): a member IS a portal_grant row with role_label='mitglied',
 * role ∈ PORTAL_MEMBER_ROLES and the FULL scope set (identical rights — the role gates
 * ONLY member management). Field names therefore follow the grant columns:
 *   status      — PORTAL_GRANT_STATUS values ('invited' | 'active' | 'revoked');
 *                 'revoked' = deactivated (UI label "Deaktiviert").
 *   revoked_at  — deactivation timestamp (null while invited/active).
 *   is_self     — true when this row is the CALLING session's own member grant.
 *                 Server-computed capability flag (coding-standards §3: the API tells
 *                 the UI — the FE hides the self-deactivate action from THIS flag and
 *                 the server independently rejects self-deactivation with 409).
 *
 * NEVER a raw row: tenant_id / subject ids / scopes are not on the wire (scopes carry
 * no information — members always hold the full set).
 *
 * [Source: Story V10-P AC 1; apps/api/src/modules/customer-portal/portal-grant.service.ts#PortalGrantResponse]
 */
export interface PortalMemberResponse {
  id:           string;
  email:        string;
  display_name: string | null;
  role:         PortalMemberRole;
  status:       string;
  invited_at:   string;
  activated_at: string | null;
  revoked_at:   string | null;
  is_self:      boolean;
}

// ── Story V10-Q — Portal policies read (Meine Versicherungen, Kap. 20.7.2) ────

/**
 * PortalPolicyListItemDto — one row of GET /portal/policies (Story V10-Q).
 *
 * The customer-facing policy read shape (Kap. 20.7.2): Versicherer via the global
 * company_master join, Sparte, Prämie, Laufzeit/Ablauf, Vertragsstatus.
 *
 * Wire rules (rule #1 + coding-standards §1):
 *   - `tenant_id` / `deleted_at` / subject ids are NEVER on this wire.
 *   - `status` is never 'draft' (drafts are excluded server-side).
 *   - `premium_annual_gross` = the CURRENT policy_version's premium_gross
 *     (valid_to IS NULL) as a numeric(14,2) string — the SAME value the broker
 *     Police detail shows; null when the current version carries no premium.
 *
 * [Source: docs/stories/v10-q-portal-policies.md AC 2]
 * [Source: packages/shared/src/policy.ts#PolicyDto] (snake_case wire precedent)
 */
export interface PortalPolicyListItemDto {
  id: string;
  policy_number: string;
  carrier_uid: string | null;
  carrier_name: string | null;          // company_master.name via the uid join; null when unmatched
  line_of_business: LineOfBusiness | null;
  status: PolicyStatus;                 // never 'draft' on this wire
  inception_date: string | null;        // ISO date
  expiry_date: string | null;           // ISO date
  currency: string;
  payment_frequency: PaymentFrequency | null;
  premium_annual_gross: string | null;  // CURRENT version premium_gross, numeric(14,2) string
}

/**
 * PortalPolicyDocumentDto — one linked policy document in the detail response
 * (the `document` rows referenced by the policy's policy_version.source_document_id
 * provenance links). `blob_url` is NEVER on the wire — bytes are served by the
 * download endpoint through the OBJECT_STORE seam.
 *
 * [Source: docs/stories/v10-q-portal-policies.md AC 3]
 */
export interface PortalPolicyDocumentDto {
  id: string;
  filename: string;
  mime_type: string | null;
  size_bytes: number | null;
  kind: string | null;                  // document.kind, e.g. 'police_neu' | 'police_mutation'
  created_at: string;
}

/**
 * PortalPolicyDetailDto — GET /portal/policies/:id (Story V10-Q).
 *
 * Extends the list item with the Kündigungsfrist block + the linked documents:
 *   notice_period_days — the tenant's resolved renewal_settings.notice_period_days
 *                        (in-code default 90 when no row is stored);
 *   cancel_by          — SERVER-computed expiry_date − notice_period_days
 *                        (rule #2 — no date math in the UI); null when no expiry.
 *
 * [Source: docs/stories/v10-q-portal-policies.md AC 2/3]
 */
export interface PortalPolicyDetailDto extends PortalPolicyListItemDto {
  notice_period_days: number;           // resolved renewal_settings (default 90)
  cancel_by: string | null;             // expiry_date − notice_period_days; null when no expiry
  documents: PortalPolicyDocumentDto[];
}

// ── Story V10-R — Portal invoices read (Rechnungen, Kap. 20.7.13) ─────────────

/**
 * PORTAL_INVOICE_STATUS_VALUES — the FIXED customer-facing invoice status set
 * (Story V10-R, D-PORTAL-INVOICE-STATUS).
 *
 * The raw broker lifecycle enum (invoice_check_status: received → matched → checked →
 * released | in_review | blocked | disputed → resolved → paid) is NEVER on the portal
 * wire — `blocked`/`disputed`/`in_review` would leak the broker's escalation state.
 * The api maps it to these four values (mapper in apps/api — business decision in
 * NestJS, rule #2). ASCII identifiers; umlaut labels ("In Prüfung") live in the UI only.
 *
 * [Source: docs/stories/v10-r-portal-invoices.md AC 2]
 */
export const PORTAL_INVOICE_STATUS_VALUES = [
  'offen',
  'in_pruefung',
  'geprueft',
  'bezahlt',
] as const;

/** Union type of the four customer-facing portal invoice statuses. */
export type PortalInvoiceStatus = (typeof PORTAL_INVOICE_STATUS_VALUES)[number];

/**
 * PortalInvoiceItemDto — one row of GET /portal/invoices (Story V10-R, Kap. 20.7.13).
 *
 * Customer-scoped via the matched-policy subject fence: only invoices whose matched
 * policy belongs to the session subject appear — `policy_id` is therefore non-null.
 *
 * Wire rules (AC 2 — deliberately narrower than the broker InvoiceListItemDto):
 *   - `status` is the MAPPED PortalInvoiceStatus — never the raw broker enum.
 *   - Explicitly ABSENT: check_category, check_status, expected_premium,
 *     invoiced_premium, deviation_*, carrier_uid, subject_*, kunde_name, tenant_id
 *     (no invoice_check internals, no K-categories, no escalation state).
 *   - Money = numeric(14,2) strings (CHF), never float.
 *   - `iban` + `qr_reference` = the Zahlungsinformationen printed on the invoice
 *     (display only — NO payment flows).
 *
 * [Source: docs/stories/v10-r-portal-invoices.md AC 1/2]
 * [Source: packages/shared/src/invoice.ts#InvoiceListItemDto] (the broker superset)
 */
export interface PortalInvoiceItemDto {
  id: string;
  invoice_number: string | null;
  invoice_type: InvoiceType;            // 'recurring' | 'final_settlement'
  status: PortalInvoiceStatus;          // MAPPED — never the raw broker enum
  amount_gross: string | null;          // numeric(14,2) as string (CHF)
  amount_net: string | null;
  due_date: string | null;              // Fälligkeit (ISO date)
  period_from: string | null;
  period_to: string | null;
  policy_id: string;                    // non-null: only matched invoices appear
  policy_number: string | null;
  carrier_name: string | null;          // company_master.name; null if carrier unmatched
  has_document: boolean;                // download available
  iban: string | null;                  // Zahlungsinformationen (display only)
  qr_reference: string | null;
  created_at: string;
}

/** GET /portal/invoices response envelope (Story V10-R). */
export interface PortalInvoiceListResponseDto {
  items: PortalInvoiceItemDto[];
}

// ── Story V10-T — Portal claims (Schadenmeldung v1, Kap. 20.7.8) ──────────────

/**
 * PORTAL_CLAIM_STATUS — the three states of a portal Schadenmeldung lifecycle
 * (Story V10-T, Kap. 20.7.8 "Bearbeitungsstatus").
 *
 * eingereicht → in_bearbeitung → abgeschlossen.
 * Status transitions are driven by the BROKER's task lifecycle (D-T-TASK-SYNC:
 * in_progress/reopen → in_bearbeitung; complete/cancel → abgeschlossen) —
 * the portal customer never sets a status directly.
 *
 * TEXT, not a PG enum — state machine lives in NestJS (mirrors PORTAL_GRANT_STATUS).
 *
 * [Source: docs/stories/v10-t-portal-claims.md AC 1/5]
 */
export const PORTAL_CLAIM_STATUS = {
  EINGEREICHT:    'eingereicht',
  IN_BEARBEITUNG: 'in_bearbeitung',
  ABGESCHLOSSEN:  'abgeschlossen',
} as const;

/** Union type of all valid portal claim status values. */
export type PortalClaimStatusValue = (typeof PORTAL_CLAIM_STATUS)[keyof typeof PORTAL_CLAIM_STATUS];

/** Flat readonly array of all portal claim status values (in lifecycle order). */
export const PORTAL_CLAIM_STATUS_VALUES = Object.values(PORTAL_CLAIM_STATUS) as readonly PortalClaimStatusValue[];

// ── Access retention default ──────────────────────────────────────────────────

/**
 * DEFAULT_ACCESS_RETENTION_DAYS — default access retention in days.
 *
 * 3650 = 10 years (decided 2026-06-26): portal access persists 10 years after the
 * Kundenbeziehung ends, covering the Aufbewahrungspflicht (FINMA data retention).
 * Admins may still configure a different value (0–3650) via the admin PUT.
 *
 * [Source: Story C1 spec §1.1 — access_retention_days column]
 */
export const DEFAULT_ACCESS_RETENTION_DAYS = 3650 as const;
